import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import VINDecoder from '../components/VINDecoder';
import ProductCard from '../components/ProductCard';
import { featuredDeals } from '../mock/mockData';
import { Package, TruckIcon, ShieldCheck, Headphones, ArrowRight, Car } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { productAPI, vehicleAPI } from '../utils/api';

const Homepage = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [carMakes, setCarMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const productsRes = await productAPI.getProducts({ limit: 8, sort_by: 'rating' });
      
      const transformedProducts = productsRes.data.products.map(product => ({
        ...product,
        inStock: product.in_stock !== undefined ? product.in_stock : true,
        stockQuantity: product.stock_quantity || 0,
        partNumber: product.part_number || '',
        originalPrice: product.original_price || null,
        weightKg: product.weight_kg || 0
      }));
      
      setFeaturedProducts(transformedProducts);

      const categoriesRes = await productAPI.getCategories();
      setCategories(categoriesRes.data || []);

      const brandsRes = await productAPI.getBrands();
      setBrands(brandsRes.data || []);

      const makesRes = await vehicleAPI.getMakes();
      setCarMakes(makesRes.data.makes);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: t('common.error'),
        description: t('common.loading'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddToCart = (product) => {
    onAddToCart(product);
    toast({
      title: t('cart.add'),
      description: `${product.name} ${t('products.add_to_cart')}`,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('home.hero_title')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('home.hero_subtitle')}
            </p>
          </div>
        </div>
      </section>
      
      {/* VIN Decoder Section - Find Compatible Parts */}
      <section className="py-12 bg-orange-50 border-y border-orange-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
              <Car className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-semibold text-orange-700">{t('home.find_compatible')}</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {t('vin.enter_vin')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('vin.subtitle')}
            </p>
          </div>
          
          <VINDecoder 
            onVehicleDecoded={(vehicle) => {
              toast({
                title: t('vin.vehicle_decoded'),
                description: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
              });
              setTimeout(() => {
                navigate(`/products?year=${vehicle.year}&make=${vehicle.make}&model=${vehicle.model}`);
              }, 1500);
            }}
            showFullInfo={false}
          />
          
          <div className="text-center mt-6">
            <Button 
              variant="link" 
              onClick={() => navigate('/vin-lookup')}
              className="text-orange-600 hover:text-orange-700"
            >
              {t('vin.full_decoder_tool')} <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('nav.products_count')}</h3>
                <p className="text-sm text-gray-600">{t('home.huge_selection')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="bg-green-100 p-3 rounded-lg">
                <TruckIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('home.free_delivery')}</h3>
                <p className="text-sm text-gray-600">{t('home.delivery_subtitle')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="bg-purple-100 p-3 rounded-lg">
                <ShieldCheck className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('home.secure_payment')}</h3>
                <p className="text-sm text-gray-600">{t('home.payment_subtitle')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="bg-orange-100 p-3 rounded-lg">
                <Headphones className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('home.expert_support')}</h3>
                <p className="text-sm text-gray-600">{t('home.support_subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{t('home.categories')}</h2>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/products')}
              className="text-blue-600 hover:text-blue-700"
            >
              {t('home.view_all')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((category) => (
              <Card 
                key={category.id}
                className="group hover:shadow-lg transition-all duration-300 cursor-pointer border-2 hover:border-blue-200"
                onClick={() => navigate(`/products?category=${category.name}`)}
              >
                <CardContent className="p-6 text-center">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg mb-4 mx-auto w-16 h-16 flex items-center justify-center group-hover:from-blue-100 group-hover:to-blue-200 transition-colors">
                    {category.icon ? (
                      category.icon.startsWith('http') ? (
                        <img src={category.icon} alt={category.name} className="w-8 h-8 object-contain" />
                      ) : (
                        <span className="text-2xl">{category.icon}</span>
                      )
                    ) : (
                      <Package className="h-8 w-8 text-blue-600" />
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
                  <p className="text-sm text-gray-600">{t('common.view')}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">{t('home.featured_deals')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredDeals.map((deal) => (
              <Card key={deal.id} className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-300">
                <div className="relative h-48">
                  <img 
                    src={deal.image} 
                    alt={deal.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-bold text-white mb-2">{deal.title}</h3>
                    <p className="text-xl font-bold text-yellow-400">{deal.discount}</p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 mb-3 text-white/90">{t('common.add')} {deal.products.join(', ')}</p>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    onClick={() => navigate('/products')}
                  >
                    {t('home.shop_now')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{t('home.bestselling_products')}</h2>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/products')}
              className="text-blue-600 hover:text-blue-700"
            >
              {t('home.view_all')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Top Brands */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('home.top_brands')}</h2>
          
        <div className="grid grid-cols-3 md:grid-cols-5 gap-6">
            {brands.slice(0, 10).map((brand) => (
              <Card 
                key={brand.id}
                className="p-6 hover:shadow-lg transition-all cursor-pointer group border-2 hover:border-blue-200"
                onClick={() => navigate(`/products?brand=${brand.name}`)}
              >
                <div className="text-center">
                  <p className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {brand.name}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Car Make */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('home.shop_by_car_make')}</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {carMakes.slice(0, 10).map((make) => (
              <Card 
                key={make.id}
                className="p-6 hover:shadow-lg transition-all cursor-pointer group border-2 hover:border-blue-200"
                onClick={() => navigate(`/products?make=${make.id}`)}
              >
                <div className="text-center">
                  <div className="mb-3 flex justify-center">
                    <img 
                      src={make.logo} 
                      alt={make.name}
                      className="h-12 w-12 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {make.name}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">{t('home.need_help')}</h2>
          <p className="text-xl mb-8">
            {t('home.select_details')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
              onClick={() => navigate('/contact')}
            >
              <Headphones className="mr-2 h-5 w-5" />
              {t('home.contact_support')}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 font-semibold"
              onClick={() => navigate('/help')}
            >
              {t('home.browse_help_center')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
